import React from 'react';
import { Box, Paper, Skeleton, Stack } from '@mui/material';

interface LoadingStateProps {
  /**
   * page  — naglowek, kafelki i karta (cala strona)
   * tiles — rzad kafelkow z liczbami
   * table — wiersze tabeli/listy w karcie
   * cards — siatka kart
   * block — pojedynczy blok tresci
   */
  variant?: 'page' | 'tiles' | 'table' | 'cards' | 'block';
  rows?: number;
  count?: number;
  height?: number;
  /** Bez wlasnej karty — do wnetrza SectionCard (dotyczy wariantow table i block). */
  bare?: boolean;
}

const Tiles: React.FC<{ count: number }> = ({ count }) => (
  <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: `repeat(${count}, minmax(0, 1fr))` } }}>
    {Array.from({ length: count }).map((_, i) => (
      <Paper key={i} sx={{ p: 2.5 }}>
        <Skeleton width="55%" height={18} />
        <Skeleton width="45%" height={38} sx={{ mt: 0.5 }} />
        <Skeleton width="70%" height={16} sx={{ mt: 0.5 }} />
      </Paper>
    ))}
  </Box>
);

const Rows: React.FC<{ rows: number; bare?: boolean }> = ({ rows, bare }) => {
  const list = (
    <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
      {Array.from({ length: rows }).map((_, i) => (
        <Stack key={i} direction="row" alignItems="center" gap={2} sx={{ px: 2.5, py: 1.5 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width={`${40 + ((i * 17) % 35)}%`} height={18} />
            <Skeleton width={`${25 + ((i * 11) % 20)}%`} height={14} />
          </Box>
          <Skeleton width={72} height={24} variant="rounded" />
        </Stack>
      ))}
    </Stack>
  );
  if (bare) return list;
  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, py: 1.75, borderBottom: 1, borderColor: 'divider' }}>
        <Skeleton width={180} height={20} />
      </Box>
      {list}
    </Paper>
  );
};

/** Szkielet tresci zamiast spinnera — uklad nie przeskakuje po zaladowaniu danych. */
export const LoadingState: React.FC<LoadingStateProps> = ({ variant = 'block', rows = 5, count = 4, height = 220, bare }) => {
  if (variant === 'tiles') return <Tiles count={count} />;
  if (variant === 'table') return <Rows rows={rows} bare={bare} />;
  if (variant === 'cards') {
    return (
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' } }}>
        {Array.from({ length: count }).map((_, i) => (
          <Paper key={i} sx={{ p: 2.5 }}>
            <Stack direction="row" gap={1.5} alignItems="center">
              <Skeleton variant="circular" width={44} height={44} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="60%" height={20} />
                <Skeleton width="40%" height={16} />
              </Box>
            </Stack>
            <Skeleton height={16} sx={{ mt: 2 }} />
            <Skeleton width="80%" height={16} />
          </Paper>
        ))}
      </Box>
    );
  }
  if (variant === 'page') {
    return (
      <Stack gap={2.5} aria-busy="true" aria-label="Ładowanie">
        <Tiles count={count} />
        <Rows rows={rows} />
      </Stack>
    );
  }
  if (bare) {
    return (
      <Box aria-busy="true" aria-label="Ładowanie">
        <Skeleton variant="rounded" height={height} />
      </Box>
    );
  }
  return (
    <Paper sx={{ p: 2.5 }} aria-busy="true" aria-label="Ładowanie">
      <Skeleton width="35%" height={22} />
      <Skeleton variant="rounded" height={height} sx={{ mt: 1.5 }} />
    </Paper>
  );
};

export default LoadingState;
